import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import { PreviewIcon } from '@/src/ui';
import { sanitizeGradeShortCode } from '@/src/utils';
import { useAuth } from '@/src/app/context/useAuth';

interface CollapsiblePanelProps {
    title: React.ReactNode;
    children: React.ReactNode;
    link?: string;
    tooltip?: string;
    headerTextColor?: string;
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({
    title,
    children,
    link,
    tooltip,
    headerTextColor,
}) => {
    const [open, setOpen] = useState(true);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState<string>('none');

    const { user } = useAuth();

    // -- Animation Logic --
    useEffect(() => {
        if (!contentRef.current) return;
        const el = contentRef.current;
        const fullHeight = el.scrollHeight;

        if (open) {
            setMaxHeight(fullHeight + 'px');
            const timer = setTimeout(() => {
                setMaxHeight('none');
            }, 250);
            return () => clearTimeout(timer);
        } else {
            setMaxHeight(fullHeight + 'px');
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setMaxHeight('0px');
                });
            });
        }
    }, [open]);

    const color = user?.alternateGradeColorShortCode || user?.gradeShortCode;
    const headerBg = `var(--pm-g${sanitizeGradeShortCode(color)}-light)`;

    return (
        <div style={{ width: '100%', marginBottom: '24px', position: 'relative' }}>
            {/* HEADER */}
            <div
                onClick={() => setOpen(!open)}
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',

                    width: 'calc(100% + 12px)',
                    marginLeft: '-6px',

                    backgroundColor: headerBg,
                    color: headerTextColor || '#111827', // Default to dark text for contrast
                    padding: '12px 20px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                    zIndex: 2,
                    position: 'relative',
                    transition: 'all 0.2s ease',
                }}
            >
                {/* Title */}
                <h3
                    style={{
                        margin: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '18px',
                        fontFamily: 'AvenirHeavy',
                    }}
                >
                    {title}
                </h3>

                {/* Controls (Pinned to Right) */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        position: 'absolute',
                        right: '20px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                    }}
                >
                    {link && (
                        <Link to={link} onClick={(e) => e.stopPropagation()}>
                            {tooltip ? (
                                <Tooltip title={tooltip} arrow>
                                    <span>
                                        <PreviewIcon style={{ height: '28px', width: '28px' }} />
                                    </span>
                                </Tooltip>
                            ) : (
                                <PreviewIcon style={{ height: '28px', width: '28px' }} />
                            )}
                        </Link>
                    )}
                    <span
                        style={{
                            fontSize: '28px',
                            opacity: 0.6,
                            transition: 'transform 0.25s ease',
                            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                        }}
                    >
                        ▾
                    </span>
                </div>
            </div>

            {/* BODY */}
            <div
                ref={contentRef}
                style={{
                    overflow: 'hidden',
                    maxHeight: maxHeight,
                    opacity: open ? 1 : 0,
                    transition: 'max-height 0.25s ease, opacity 0.25s ease',

                    backgroundColor: '#F5F5F5',
                    border: '1px solid #ddd',
                    marginTop: '-8px',
                    paddingTop: '8px',
                    borderBottomLeftRadius: '12px',
                    borderBottomRightRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    zIndex: 1,
                    position: 'relative',
                }}
            >
                <div style={{ padding: '16px' }}>{children}</div>
            </div>
        </div>
    );
};

export default CollapsiblePanel;
